import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminPasswordField from '../components/security/AdminPasswordField';
import ResetPasswordModal from '../components/security/ResetPasswordModal';
import AgencyColorsCard from '../components/agency/AgencyColorsCard';
import AgencyBaremeCard from '../components/agency/AgencyBaremeCard';
import AgencyMentionsLegalesCard from '../components/agency/AgencyMentionsLegalesCard';
import AgencyImportExportCard from '../components/agency/AgencyImportExportCard';
import { PASSWORD_MIN_LENGTH } from '../utils/passwordStrength';
import { authHeaderOrEmpty } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  ...authHeaderOrEmpty(),
});

// ============================================
// User Modal Component
// ============================================
const UserModal = ({ user, agenceId, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    email: user?.email || '',
    password: '',
    nom: user?.nom || '',
    prenom: user?.prenom || '',
    telephone: user?.telephone || '',
    role: user?.role || 'agent'
  });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [pwdValidity, setPwdValidity] = useState({ tooShort: true, pwned: false, checking: false });

  const roles = [
    { value: 'agent', label: 'Agent immobilier' },
    { value: 'secretaire', label: 'Secrétaire' },
    { value: 'comptable', label: 'Comptable' },
    { value: 'agent_co', label: 'Agent commercial' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setModalError('');
    if (!user && (pwdValidity.tooShort || pwdValidity.pwned || pwdValidity.checking)) {
      setModalError(`Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères et ne pas être compromis.`);
      return;
    }
    setSaving(true);

    try {
      const url = user 
        ? `${API_URL}/api/admin-agence/users/${user.id}`
        : `${API_URL}/api/admin-agence/users`;
      
      const body = user 
        ? { nom: formData.nom, prenom: formData.prenom, telephone: formData.telephone, role: formData.role }
        : { ...formData, agence_id: agenceId };

      const response = await fetch(url, {
        method: user ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        onSave(data);
        onClose();
      } else {
        setModalError(data.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setModalError('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-6 py-4 rounded-t-2xl">
          <h2 className="text-xl font-bold text-white">
            {user ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {modalError && (
            <div className="bg-red-100 border border-red-300 rounded-lg p-3 text-red-700 text-sm">
              {modalError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Prénom *</label>
              <input
                type="text"
                value={formData.prenom}
                onChange={(e) => handleChange('prenom', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom *</label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => handleChange('nom', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
              />
            </div>
            
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                required
                disabled={!!user}
              />
            </div>
            
            {!user && (
              <div className="col-span-2">
                <AdminPasswordField
                  value={formData.password}
                  onChange={(v) => handleChange('password', v)}
                  onValidityChange={setPwdValidity}
                  testid="agence-user-password"
                />
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rôle *</label>
              <select
                value={formData.role}
                onChange={(e) => handleChange('role', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                {roles.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Téléphone</label>
              <input
                type="tel"
                value={formData.telephone}
                onChange={(e) => handleChange('telephone', e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || (!user && (pwdValidity.tooShort || pwdValidity.pwned || pwdValidity.checking))}
              className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : (user ? 'Modifier' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================
// Main Admin Agence Page Component
// ============================================
const AdminAgencePage = () => {
  const navigate = useNavigate();
  const [agence, setAgence] = useState(null);
  // lot CA — version du logo, figée au montage : elle ne bouge QU'après un envoi réussi.
  const [logoVersion, setLogoVersion] = useState(() => Date.now());
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(() => {
    return JSON.parse(localStorage.getItem('user') || '{}');
  });

  // Check auth on mount
  useEffect(() => {
    // Re-read user from localStorage in case it changed
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUser(storedUser);
    
    if (!['admin_agence', 'super_admin'].includes(storedUser.role)) {
      navigate('/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger l'agence et ses utilisateurs
      const response = await fetch(`${API_URL}/api/admin-agence/dashboard`, { 
        headers: getAuthHeaders() 
      });

      if (response.ok) {
        const data = await response.json();
        setAgence(data.agence);
        setUsers(data.users);
      } else if (response.status === 401) {
        navigate('/login');
      } else {
        setError('Erreur de chargement des données');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      }
    } catch (_) { /* best-effort */ }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleDeleteUser = async (user) => {
    if (user.id === currentUser.id) {
      alert('Vous ne pouvez pas supprimer votre propre compte');
      return;
    }
    
    if (!window.confirm(`Supprimer l'utilisateur "${user.prenom} ${user.nom}" ?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/admin-agence/users/${user.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        loadData();
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de la suppression');
      }
    } catch (err) {
      alert('Erreur de connexion');
    }
  };

  const handleResetPassword = (user) => {
    setResetPwdUser(user);
  };

  const submitResetPassword = async (newPassword) => {
    if (!resetPwdUser) return { ok: false, error: 'Aucun utilisateur sélectionné.' };
    try {
      const response = await fetch(`${API_URL}/api/admin-agence/users/${resetPwdUser.id}/reset-password`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ new_password: newPassword })
      });
      if (response.ok) return { ok: true };
      const data = await response.json();
      return { ok: false, error: data.detail || 'Erreur lors de la réinitialisation' };
    } catch {
      return { ok: false, error: 'Erreur de connexion' };
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin_agence: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Admin' },
      agent: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Agent' },
      secretaire: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Secrétaire' },
      comptable: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Comptable' },
      agent_co: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Agent Co.' }
    };
    const badge = badges[role] || badges.agent;
    return (
      <span className={`px-2 py-1 rounded text-xs font-bold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const goToCRM = () => {
    navigate('/');
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !agence?.id) return;

    // lot B — le SVG est refusé : il s'affiche sur le site mais disparaît de tous les PDF.
    if (file.type.includes('svg') || file.name.toLowerCase().endsWith('.svg')) {
      alert("Formats acceptés : PNG ou JPG. Le SVG n'apparaîtrait pas sur vos documents PDF.");
      return;
    }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Format invalide. Acceptés : PNG, JPG, WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Logo trop volumineux (max 5 MB)');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/agences/${agence.id}/upload-logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setAgence(prev => ({ ...prev, logo_url: data.logo_url }));
        setLogoVersion(Date.now());   // lot CA — le nouveau logo s'affiche, pas l'ancien en cache
        // Reset file input so the same file can be re-selected later
        e.target.value = '';
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de l\'upload du logo');
      }
    } catch (err) {
      alert('Erreur de connexion');
    }
  };

  const handleLogoDelete = async () => {
    if (!agence?.id) return;
    if (!window.confirm('Supprimer le logo de l\'agence ?')) return;
    try {
      const response = await fetch(`${API_URL}/api/agences/${agence.id}/logo`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setAgence(prev => ({ ...prev, logo_url: null }));
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (err) {
      alert('Erreur de connexion');
    }
  };

  const handleLogoDarkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !agence?.id) return;

    // lot B — le SVG est refusé : il s'affiche sur le site mais disparaît de tous les PDF.
    if (file.type.includes('svg') || file.name.toLowerCase().endsWith('.svg')) {
      alert("Formats acceptés : PNG ou JPG. Le SVG n'apparaîtrait pas sur vos documents PDF.");
      return;
    }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowed.includes(file.type)) {
      alert('Format invalide. Acceptés : PNG, JPG, WebP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('Logo trop volumineux (max 2 MB)');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/agences/${agence.id}/logo-dark`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (response.ok) {
        const data = await response.json();
        setAgence(prev => ({
          ...prev,
          branding_settings: { ...(prev.branding_settings || {}), logo_url_dark: data.logo_url_dark }
        }));
        setLogoVersion(Date.now());   // lot CA
        e.target.value = '';
      } else {
        const data = await response.json();
        alert(data.detail || 'Erreur lors de l\'upload du logo clair');
      }
    } catch (err) {
      alert('Erreur de connexion');
    }
  };

  const buildLogoSrc = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    // Route PUBLIQUE (une <img> ne peut pas envoyer le token → /api/media/* renverrait 401)
    const fname = url.split('/').pop();
    // lot CA — 🔴 l'URL portait un horodatage calculé PENDANT le rendu, donc neuf à chaque fois :
    // le navigateur redemandait le logo en boucle et l'annulait aussitôt (image jamais affichée).
    // `logoVersion` ne change qu'après un envoi réussi : le cache est contourné quand il le faut,
    // et une seule fois.
    return `${API_URL}/api/public/agence-logo/${fname}?v=${logoVersion}`;
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-emerald-600 to-teal-600 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 py-3 md:py-0 md:h-16">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="bi bi-building text-white text-lg"></i>
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold text-white">Administration Agence</h1>
                <p className="text-xs text-emerald-100 truncate">{agence?.nom || 'Chargement...'}</p>
              </div>
            </div>
            
            {/* lot DK — téléphone : « Accéder au CRM » sous le nom, identité masquée */}
            <div className="flex items-center gap-3 md:gap-4">
              <button
                onClick={goToCRM}
                className="px-4 h-11 md:h-auto md:py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition flex items-center gap-2"
              >
                <i className="bi bi-grid"></i>
                Accéder au CRM
              </button>
              <div className="text-right hidden md:block">
                <p className="text-sm font-medium text-white">{currentUser.prenom} {currentUser.nom}</p>
                <p className="text-xs text-emerald-200">{currentUser.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-emerald-200 hover:text-white hover:bg-white/10 rounded-lg transition"
                title="Déconnexion"
              >
                <i className="bi bi-box-arrow-right text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="bg-red-100 border border-red-300 rounded-lg p-4 text-red-700 mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
            <i className="bi bi-arrow-repeat animate-spin text-4xl text-emerald-500"></i>
            <p className="text-slate-500 mt-4">Chargement...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Agence Info Card */}
            {agence && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    {agence.logo_url ? (
                      <div className="relative group" data-testid="agence-logo-wrapper">
                        <img
                          src={buildLogoSrc(agence.logo_url)}
                          alt={`Logo ${agence.nom}`}
                          className="w-16 h-16 rounded-2xl object-contain bg-white border border-slate-200 p-1"
                          data-testid="agence-logo-img"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-white font-bold text-2xl">
                        {agence.code?.substring(0, 2) || 'AG'}
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-bold text-slate-800">{agence.nom}</h2>
                      <p className="text-sm text-slate-500">
                        Code: <span className="font-mono font-bold text-emerald-600">{agence.code}</span>
                        {agence.ville && ` • ${agence.ville}`}
                      </p>
                      {agence.email && (
                        <p className="text-sm text-slate-500">{agence.email}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6 flex-wrap">
                    {/* Logo management */}
                    <div className="flex items-center gap-2">
                      <label
                        className="px-3 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition flex items-center gap-2 cursor-pointer text-sm"
                        title="Uploader / remplacer le logo (PNG ou JPG, WebP accepté, max 5 Mo). Le SVG n'apparaîtrait pas sur vos PDF."
                        data-testid="agence-logo-upload-btn"
                      >
                        <i className="bi bi-cloud-arrow-up"></i>
                        {agence.logo_url ? 'Remplacer le logo' : 'Ajouter un logo'}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp"
                          onChange={handleLogoUpload}
                          className="hidden"
                          data-testid="agence-logo-upload-input"
                        />
                      </label>
                      {agence.logo_url && (
                        <button
                          onClick={handleLogoDelete}
                          className="w-11 h-11 md:w-auto md:h-auto md:p-2 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Supprimer le logo"
                          data-testid="agence-logo-delete-btn"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      )}
                    </div>
                    {/* Logo clair (page de connexion sombre) */}
                    <div className="flex items-center gap-2">
                      {agence.branding_settings?.logo_url_dark && (
                        <img
                          src={buildLogoSrc(agence.branding_settings.logo_url_dark)}
                          alt="Logo clair"
                          className="w-10 h-10 rounded-lg object-contain bg-slate-900 border border-slate-700 p-1"
                          data-testid="agence-logo-dark-img"
                        />
                      )}
                      <label
                        className="px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium transition flex items-center gap-2 cursor-pointer text-sm"
                        title="Logo en version claire, affiché sur la page de connexion à fond sombre (PNG, JPG, WebP, SVG, max 2 Mo)"
                        data-testid="agence-logo-dark-upload-btn"
                      >
                        <i className="bi bi-moon-stars"></i>
                        {agence.branding_settings?.logo_url_dark ? 'Logo clair' : 'Ajouter un logo clair'}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          onChange={handleLogoDarkUpload}
                          className="hidden"
                          data-testid="agence-logo-dark-upload-input"
                        />
                      </label>
                    </div>
                    <div className="text-center">
                      <p className="text-3xl font-bold text-emerald-600">{users.length}</p>
                      <p className="text-sm text-slate-500">utilisateurs</p>
                    </div>
                    <span className="px-4 py-2 rounded-full text-sm font-bold bg-emerald-100 text-emerald-700">
                      {agence.statut}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Couleurs de l'agence (co-branding self-service) */}
            {agence && (
              <AgencyColorsCard
                agence={agence}
                onSaved={(colors) => setAgence(prev => ({
                  ...prev,
                  branding_settings: {
                    ...(prev.branding_settings || {}),
                    couleur_primaire: colors.couleur_primaire,
                    couleur_secondaire: colors.couleur_secondaire,
                  }
                }))}
              />
            )}

            {/* Barème d'honoraires (PDF uploadé ou URL du site) */}
            {agence && (
              <AgencyBaremeCard
                agence={agence}
                onSaved={(patch) => setAgence(prev => ({ ...prev, ...patch }))}
              />
            )}

            {/* Mentions légales (lot DA — en-tête du bon de visite) */}
            {agence && currentUser.role === 'admin_agence' && (
              <AgencyMentionsLegalesCard
                agence={agence}
                onSaved={(patch) => setAgence((prev) => ({ ...prev, ...patch }))}
              />
            )}

            {/* Import / Export de l'agence (lot CB — module import-export) */}
            <AgencyImportExportCard />

            {/* Users List */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800">Utilisateurs de l'agence</h2>
                <button
                  onClick={() => { setEditingUser(null); setShowUserModal(true); }}
                  className="px-4 h-11 md:h-auto md:py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                >
                  <i className="bi bi-person-plus"></i>
                  Nouvel utilisateur
                </button>
              </div>

              {users.length === 0 ? (
                <div className="p-12 text-center">
                  <i className="bi bi-people text-5xl text-slate-300"></i>
                  <p className="text-slate-500 mt-4">Aucun utilisateur</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {users.map((user) => (
                    <div key={user.id} className="p-4 hover:bg-slate-50 transition">
                      {/* lot DK — téléphone : une carte par utilisateur, rien de coupé */}
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-12 h-12 flex-shrink-0 bg-gradient-to-br from-slate-400 to-slate-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {user.prenom?.[0]}{user.nom?.[0]}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-slate-800 break-words">{user.prenom} {user.nom}</h3>
                            <p className="text-sm text-slate-500 break-all">{user.email}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-2 md:gap-4">
                          {getRoleBadge(user.role)}
                          
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            user.statut === 'actif' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {user.statut}
                          </span>
                          
                          <div className="flex gap-1 ml-auto md:ml-0">
                            <button
                              onClick={() => { setEditingUser(user); setShowUserModal(true); }}
                              className="w-11 h-11 md:w-auto md:h-auto md:p-2 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Modifier"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              onClick={() => handleResetPassword(user)}
                              className="w-11 h-11 md:w-auto md:h-auto md:p-2 flex items-center justify-center text-amber-600 hover:bg-amber-50 rounded-lg transition"
                              title="Réinitialiser mot de passe"
                            >
                              <i className="bi bi-key"></i>
                            </button>
                            {user.id !== currentUser.id && (
                              <button
                                onClick={() => handleDeleteUser(user)}
                                className="w-11 h-11 md:w-auto md:h-auto md:p-2 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition"
                                title="Supprimer"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* User Modal */}
      {showUserModal && (
        <UserModal
          user={editingUser}
          agenceId={agence?.id}
          onClose={() => { setShowUserModal(false); setEditingUser(null); }}
          onSave={() => loadData()}
        />
      )}

      {resetPwdUser && (
        <ResetPasswordModal
          userName={`${resetPwdUser.prenom} ${resetPwdUser.nom}`}
          onClose={() => setResetPwdUser(null)}
          onSubmit={submitResetPassword}
        />
      )}
    </div>
  );
};

export default AdminAgencePage;
